import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from '../services/apiClient';
import { useQuery } from '@tanstack/react-query';
import StakeTable, { Column } from './GameEngine/StakeTable';
import { FormMultiSelect } from '../components/ui';
import { DateRangeFilter } from '../components/ui/DateRangeFilter';

// ─── Types ────────────────────────────────────────────────────────────────────
type ReportUser = {
  id: string;
  name: string;
  phone: string | null;
  email: string;
  created: string;
  ip_address: string | null;
  geo: string | null;
  is_collaborator: boolean;
  is_blocked: boolean;
  parent_name: string | null;
  parent_role: string | null;
  affiliate_name: string | null;
  affiliate_email: string | null;
  manager_name: string | null;
  manager_email: string | null;
  // financials
  total_deposit: Record<string, number>;
  total_withdrawal: Record<string, number>;
  deposit_count: number;
  withdrawal_count: number;
  ftd: number;           // first deposit amount
  ftd_currency: string | null; // first deposit currency
  ftd_date: string | null; // first deposit date
  total_wagered: Record<string, number>; // total bets
  profile: any;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtDate = (d: string | null) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const fmtAmt = (n: number | undefined | null) =>
  (!n || isNaN(Number(n))) ? "0.00" : Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const MultiCurrencyDisplay = ({ amounts, rates }: { amounts: Record<string, number> | null, rates?: Record<string, number> }) => {
  if (!amounts || Object.keys(amounts).length === 0) return <span className="text-gray-500">—</span>;
  return (
    <div className="flex flex-col gap-1 max-h-24 overflow-y-auto pr-1">
      {Object.entries(amounts).map(([currency, amt]) => {
        let fiatVal = null;
        if (rates && currency !== 'USD') {
          if (currency === 'INR') {
            fiatVal = amt / (rates['INR'] || 83.5);
          } else {
            const rate = rates[currency] || rates[currency.replace(/TRC20|ERC20|BEP20|MATIC/g, '')];
            if (rate) {
              fiatVal = amt * rate; // approximate USD
            }
          }
        }
        return (
          <span key={currency} className="text-xs font-semibold px-2 py-0.5 bg-slate-800 rounded-md border border-slate-700 whitespace-nowrap flex items-center justify-between gap-2">
            <span className="uppercase text-slate-400 mr-1">{currency}:</span> 
            <span className="text-white flex flex-col items-end">
              <span>{fmtAmt(amt)}</span>
              {fiatVal !== null && currency !== 'USD' && (
                <span className="text-[9px] text-gray-500 font-mono">≈ ${fmtAmt(fiatVal)}</span>
              )}
            </span>
          </span>
        );
      })}
    </div>
  );
};

const ALL_COLUMNS = [
  { id: "sn", label: "SN" },
  { id: "uid", label: "UID" },
  { id: "phone", label: "Phone" },
  { id: "username", label: "Username" },
  { id: "email", label: "Email" },
  { id: "reg_date", label: "Reg Date" },
  { id: "geo", label: "Geo" },
  { id: "ip", label: "IP" },
  { id: "ftd_date", label: "FTD Date" },
  { id: "ftd", label: "FTD" },
  { id: "total_wagered", label: "Total Wagered" },
  { id: "total_deps", label: "Deposits" },
  { id: "deps_count", label: "Dep Count" },
  { id: "total_wd", label: "Withdrawals" },
  { id: "wd_count", label: "W/D Count" },
  { id: "profile", label: "Registration Details" },
  { id: "affiliate", label: "Affiliate" },
  { id: "manager", label: "Manager" },
  { id: "actions", label: "Actions" },
];

// ─── Component ────────────────────────────────────────────────────────────────
const UserReports: React.FC = () => {
  const navigate = useNavigate();
  const currentRole = (localStorage.getItem("role") || "").toLowerCase();

  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    const role = (localStorage.getItem("role") || "").toLowerCase();
    if (role === 'affiliate') {
      return ALL_COLUMNS.filter(c => c.id !== 'actions' && c.id !== 'affiliate' && c.id !== 'manager').map(c => c.id);
    }
    return ALL_COLUMNS.map(c => c.id);
  });

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [collabFilter, setCollabFilter] = useState<string[]>([]);
  const [subadminFilter, setSubadminFilter] = useState<string[]>([]);
  const [adminFilter, setAdminFilter] = useState<string[]>([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const { data: filtersData } = useQuery({
    queryKey: ['reportFilters'],
    queryFn: async () => {
      const token = localStorage.getItem("token");
      const [affRes, mgrRes] = await Promise.all([
        apiClient.get(`/collaborator/list?role=affiliate`, { headers: { Authorization: `Bearer ${token}` } }),
        apiClient.get(`/collaborator/managers`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      const affData = affRes.data?.data || [];
      const mgrData = mgrRes.data?.data || [];
      
      return {
        affiliates: affData.map((a: any) => ({
          username: a.username,
          displayLabel: a.username || '',
          email: a.email || ''
        })),
        managers: mgrData.map((m: any) => ({
          username: m.username,
          displayLabel: m.username || '',
          email: m.email || ''
        }))
      };
    }
  });

  const collaborators = filtersData?.affiliates || [];
  const subadmins = filtersData?.managers || [];

  const { data: authForms = [] } = useQuery({
    queryKey: ['authForms'],
    queryFn: async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await apiClient.get('/admin/auth-forms', {
          headers: { Authorization: `Bearer ${token}` }
        });
        return response.data?.status ? response.data.data : [];
      } catch (e) {
        console.error(e);
        return [];
      }
    }
  });

  const customFieldLabels = React.useMemo(() => {
    const labelsMap: Record<string, string> = {};
    authForms.forEach((form: any) => {
      const customFields = form.fields_config?.custom_fields || [];
      customFields.forEach((field: any) => {
        if (field.id && field.label) {
          labelsMap[field.id] = field.label;
          const rawId = field.id.replace(/^field_/, '');
          labelsMap[rawId] = field.label;
        }
      });
    });
    return labelsMap;
  }, [authForms]);

  const { data: reportData, isLoading } = useQuery({
    queryKey: ['reports', page, limit, search, adminFilter, subadminFilter, collabFilter, startDate, endDate],
    queryFn: async () => {
      const token = localStorage.getItem("token");
      let url = `/getUserDataByAdminReport?page=${page}&limit=${limit}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (adminFilter.length > 0) url += `&admins=${encodeURIComponent(adminFilter.join(','))}`;
      if (subadminFilter.length > 0) url += `&managers=${encodeURIComponent(subadminFilter.join(','))}`;
      if (collabFilter.length > 0) url += `&affiliates=${encodeURIComponent(collabFilter.join(','))}`;
      if (startDate) url += `&startDate=${encodeURIComponent(startDate)}`;
      if (endDate) url += `&endDate=${encodeURIComponent(endDate)}`;

      const res = await apiClient.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const responseData = res.data?.data || [];
      const pagination = res.data?.pagination || { total: 0, totalPages: 1 };

      const mapped: ReportUser[] = responseData.map((u: any) => ({
        id: String(u.id),
        name: u.name ?? "—",
        phone: u.phone ?? null,
        email: u.email ?? "—",
        created: u.created ?? u.created_at ?? "",
        ip_address: u.ip_address ?? null,
        geo: u.geo ?? null,
        is_collaborator: Boolean(u.is_collaborator),
        is_blocked: Boolean(u.is_blocked),
        parent_name: u.parent_name ?? null,
        parent_role: u.parent_role ?? null,
        affiliate_name: u.affiliate_name ?? null,
        affiliate_email: u.affiliate_email ?? null,
        manager_name: u.manager_name ?? null,
        manager_email: u.manager_email ?? null,
        total_deposit: u.total_deposit_usdt || {},
        total_withdrawal: u.total_withdrawal_usdt || {},
        deposit_count: Number(u.deposit_count) || 0,
        withdrawal_count: Number(u.withdrawal_count) || 0,
        ftd: Number(u.ftd) || 0,
        ftd_currency: u.ftd_currency ?? null,
        ftd_date: u.ftd_date ?? null,
        total_wagered: u.total_wagered || {},
        profile: u.profile || {},
      }));

      const adminNames = Array.from(new Set(mapped.filter(u => u.parent_role === 'admin').map(u => u.parent_name).filter(Boolean))).sort() as string[];
      
      return { users: mapped, admins: adminNames, rates: res.data?.rates || {}, pagination };
    }
  });

  const users = reportData?.users || [];
  const admins = reportData?.admins || [];
  const rates = reportData?.rates || {};
  const total = reportData?.pagination?.total || 0;
  const totalPages = reportData?.pagination?.totalPages || 1;

  // ─── CSV Export ───────────────────────────────────────────────────────────────
  const exportCSV = async () => {
    // For CSV, we fetch all data without pagination
    const token = localStorage.getItem("token");
    let url = `/getUserDataByAdminReport?page=1&limit=50000`; // Fetch up to 50k for CSV
    if (search) url += `&search=${encodeURIComponent(search)}`;
    if (adminFilter.length > 0) url += `&admins=${encodeURIComponent(adminFilter.join(','))}`;
    if (subadminFilter.length > 0) url += `&managers=${encodeURIComponent(subadminFilter.join(','))}`;
    if (collabFilter.length > 0) url += `&affiliates=${encodeURIComponent(collabFilter.join(','))}`;
    if (startDate) url += `&startDate=${encodeURIComponent(startDate)}`;
    if (endDate) url += `&endDate=${encodeURIComponent(endDate)}`;

    try {
      const res = await apiClient.get(url, { headers: { Authorization: `Bearer ${token}` } });
      const exportData = res.data?.data || [];

      const activeCols = ALL_COLUMNS.filter(c => 
        visibleColumns.includes(c.id) && 
        c.id !== "actions" && 
        (currentRole !== 'affiliate' || (c.id !== 'affiliate' && c.id !== 'manager'))
      );
      const headers = activeCols.map(c => c.label);

      const rows = exportData.map((u: any, i: any) => {
        return activeCols.map(c => {
          switch (c.id) {
            case "sn": return i + 1;
            case "uid": return u.id;
            case "phone": return u.phone ?? "";
            case "username": return u.profile?.name || u.name;
            case "email": return u.email;
            case "reg_date": return fmtDate(u.created);
            case "geo": 
              let geoStr = u.geo ?? "";
              if (typeof u.geo === 'string' && u.geo.startsWith('{')) {
                try {
                  const g = JSON.parse(u.geo);
                  geoStr = [g.city, g.region, g.country_name || g.country].filter(Boolean).join(', ');
                } catch(e){}
              }
              return geoStr;
            case "ip": return u.ip_address ?? "";
            case "ftd_date": return fmtDate(u.ftd_date);
            case "ftd": return u.ftd > 0 ? `${Number(u.ftd).toFixed(4)} ${u.ftd_currency || 'INR'}` : "";
            case "total_wagered": 
              const wagers = u.total_wagered || {};
              return Object.entries(wagers).map(([k,v]) => `${k}:${v}`).join(' | ');
            case "total_deps": 
              const deps = u.total_deposit_usdt || {};
              return Object.entries(deps).map(([k,v]) => `${k}:${v}`).join(' | ');
            case "deps_count": return u.deposit_count || 0;
            case "total_wd": 
              const wds = u.total_withdrawal_usdt || {};
              return Object.entries(wds).map(([k,v]) => `${k}:${v}`).join(' | ');
            case "wd_count": return u.withdrawal_count || 0;
            case "affiliate": return u.affiliate_name ? `${u.affiliate_name} (${u.affiliate_email})` : "";
            case "manager": return u.manager_name ? `${u.manager_name} (${u.manager_email})` : "";
            case "profile": return JSON.stringify(u.profile || {});
            default: return "";
          }
        });
      });

      const csv =
        "data:text/csv;charset=utf-8," +
        [headers, ...rows]
          .map(r =>
            r.map((v: any) => `"${String(v).replace(/"/g, '""')}"`).join(",")
          )
          .join("\n");

      const link = document.createElement("a");
      link.setAttribute("href", encodeURI(csv));
      link.setAttribute("download", `user_reports_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      console.error("Failed to export", e);
      alert("Failed to export report");
    }
  };

  // ─── Columns for StakeTable ────────────────────────────────────────────────────
  const columns: Column[] = ALL_COLUMNS
    .filter(c => visibleColumns.includes(c.id) && (currentRole !== 'affiliate' || (c.id !== 'actions' && c.id !== 'affiliate' && c.id !== 'manager')))
    .map(c => {
      const col: Column = { key: c.id, label: c.label };
      switch (c.id) {
        case "sn": col.render = (_, __, i) => ((page - 1) * limit) + i + 1; break;
        case "uid": col.render = (_, u: ReportUser) => <span className="font-mono text-xs">{u.id}</span>; break;
        case "phone": col.render = (val) => val ?? "—"; break;
        case "username": col.render = (_, u: ReportUser) => <span className="font-medium">{u.profile?.name || u.name}</span>; break;
        case "email": col.render = (val) => <span className="text-gray-300">{val}</span>; break;
        case "reg_date": col.render = (_, u: ReportUser) => <span className="text-gray-300 whitespace-nowrap">{fmtDate(u.created)}</span>; break;
        case "geo": col.render = (val) => {
          let geoStr = val;
          if (typeof val === 'string' && val.startsWith('{')) {
            try {
              const g = JSON.parse(val);
              geoStr = [g.city, g.region, g.country_name || g.country].filter(Boolean).join(', ');
            } catch(e){}
          }
          return <span className="text-gray-400 whitespace-nowrap text-xs">{geoStr || "—"}</span>;
        }; break;
        case "ip": col.render = (_, u: ReportUser) => <span className="text-gray-400 font-mono text-xs">{u.ip_address ?? "—"}</span>; break;
        case "ftd_date": col.render = (_, u: ReportUser) => <span className="text-gray-300 whitespace-nowrap text-xs">{fmtDate(u.ftd_date)}</span>; break;
        case "ftd": col.render = (_, u: ReportUser) => {
          if (!u.ftd) return <span className="text-gray-500">—</span>;
          const currency = u.ftd_currency || 'INR';
          let fiatVal = null;
          if (rates && currency !== 'USD') {
            if (currency === 'INR') {
              fiatVal = u.ftd / (rates['INR'] || 83.5);
            } else {
              const rate = rates[currency] || rates[currency.replace(/TRC20|ERC20|BEP20|MATIC/g, '')];
              if (rate) fiatVal = u.ftd * rate;
            }
          }
          return (
            <div className="flex flex-col items-end">
              <span className="text-emerald-400 font-semibold whitespace-nowrap">{fmtAmt(u.ftd)} <span className="text-xs text-emerald-500/70">{currency}</span></span>
              {fiatVal !== null && currency !== 'USD' ? (
                <span className="text-[10px] text-gray-500 font-mono">≈ ${fmtAmt(fiatVal)}</span>
              ) : (currency !== 'INR' && currency !== 'USD') ? (
                <span className="text-[10px] text-gray-600 font-mono italic">(Rate unavailable)</span>
              ) : null}
            </div>
          );
        }; break;
        case "total_wagered": col.render = (_, u: ReportUser) => <div className="text-indigo-400"><MultiCurrencyDisplay amounts={u.total_wagered} rates={rates} /></div>; break;
        case "total_deps": col.render = (_, u: ReportUser) => <div className="text-emerald-400"><MultiCurrencyDisplay amounts={u.total_deposit} rates={rates} /></div>; break;
        case "deps_count": col.render = (_, u: ReportUser) => <span className="text-gray-300 text-center">{u.deposit_count || "—"}</span>; col.textAlign = 'center'; break;
        case "total_wd": col.render = (_, u: ReportUser) => <div className="text-red-400"><MultiCurrencyDisplay amounts={u.total_withdrawal} rates={rates} /></div>; break;
        case "wd_count": col.render = (_, u: ReportUser) => <span className="text-gray-300 text-center">{u.withdrawal_count || "—"}</span>; col.textAlign = 'center'; break;
        case "affiliate": col.render = (_, u: ReportUser) => u.affiliate_name ? (
          <div className="flex flex-col gap-0.5"><span className="text-white text-xs font-semibold">{u.affiliate_name}</span><span className="text-gray-400 text-xs">{u.affiliate_email}</span></div>
        ) : <span className="text-gray-500 text-xs">—</span>; break;
        case "manager": col.render = (_, u: ReportUser) => u.manager_name ? (
          <div className="flex flex-col gap-0.5"><span className="text-white text-xs font-semibold">{u.manager_name}</span><span className="text-gray-400 text-xs">{u.manager_email}</span></div>
        ) : <span className="text-gray-500 text-xs">—</span>; break;
        case "profile": col.render = (_, u: ReportUser) => {
          const val = u.profile;
          if (!val || Object.keys(val).length === 0) return <span className="text-gray-500 text-xs">—</span>;
          const name = val.name || "";
          const customFields = val.custom_fields || {};
          return (
            <div className="flex flex-col gap-1 max-w-[280px] max-h-[150px] overflow-y-auto pr-1">
              {name && (
                <span className="text-xs font-semibold text-slate-300">
                  <span className="text-slate-500 uppercase mr-1 text-[10px]">Name:</span>
                  {name}
                </span>
              )}
              {Object.entries(customFields).map(([key, value]: [string, any]) => {
                const displayLabel = customFieldLabels[key] || customFieldLabels['field_' + key] || key.replace('field_', '');
                return (
                  <span key={key} className="text-[11px] bg-slate-800/60 border border-slate-700/50 rounded px-1.5 py-0.5 whitespace-pre-wrap break-all">
                    <span className="text-slate-400 font-semibold mr-1 capitalize">{displayLabel}:</span>
                    <span className="text-white">{String(value)}</span>
                  </span>
                );
              })}
              {Object.keys(customFields).length === 0 && !name && (
                <span className="text-slate-500 text-xs font-mono">{JSON.stringify(val)}</span>
              )}
            </div>
          );
        }; break;
        case "actions": col.render = (_, u: ReportUser) => (
          <button onClick={() => navigate(`/users/profile/${u.id}`)} className="text-white bg-[#646ECD] hover:bg-[#5560c0] font-medium rounded-lg text-xs px-3 py-1.5 transition-colors whitespace-nowrap">View Details</button>
        ); col.textAlign = 'center'; break;
      }
      return col;
    });

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="ml-0 lg:ml-[256px] p-4 sm:p-6 text-white min-h-screen bg-[#151929]">

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-[#1E2437] p-4 rounded-xl mb-6 shadow-sm">
        <div>
          <h2 className="text-lg font-bold text-white">Registered Users</h2>
          <p className="text-sm text-gray-400">View and manage all registered players.</p>
        </div>
        <button
          onClick={() => setShowFilters(f => !f)}
          className="flex lg:hidden items-center gap-2 px-4 py-2 bg-[#151929] border border-slate-700 rounded-lg text-sm font-semibold text-slate-300 hover:text-white hover:border-indigo-500 transition-all"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
          </svg>
          {showFilters ? 'Hide Filters' : 'Filters'}
          {(collabFilter.length + subadminFilter.length + adminFilter.length) > 0 && (
            <span className="ml-1 bg-indigo-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {collabFilter.length + subadminFilter.length + adminFilter.length}
            </span>
          )}
        </button>
      </div>
      
      {/* ── Table ───────────────────────────────────────────────────────────── */}
      <div className="mb-6">
        <StakeTable
          isLoading={isLoading}
          title=""
          columns={columns}
          data={users}
          searchPlaceholder="Search by UID, username, name, email…"
          onSearch={(val) => { setSearch(val); setPage(1); }}
          emptyMessage="No users match the current filters."
          hasSidebarMargin={false}
          page={page}
          totalPages={totalPages}
          total={total}
          limit={limit}
          onLimitChange={setLimit}
          onPageChange={setPage}
          actions={
            <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto justify-end">
              <div className="w-full sm:w-auto">
                <DateRangeFilter 
                  onFilterChange={(start, end) => {
                    setStartDate(start);
                    setEndDate(end);
                    setPage(1);
                  }} 
                />
              </div>
              <button
                onClick={exportCSV}
                className="w-full sm:w-auto justify-center bg-[#646ECD] hover:bg-[#525ab5] text-white text-sm font-medium py-2 px-3 rounded-md flex items-center gap-2 transition-colors shadow-sm whitespace-nowrap"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Export Excel
              </button>
            </div>
          }
          bottomActions={
            <div className={`flex flex-wrap gap-3 items-end ${showFilters ? 'flex' : 'hidden lg:flex'}`}>
              {[
                { 
                  label: 'Admin', 
                  hiddenRoles: ['manager', 'subadmin', 'affiliate'], 
                  value: adminFilter, 
                  setter: setAdminFilter, 
                  options: admins, 
                  defaultText: "All Admins", 
                  resetters: [setCollabFilter, setSubadminFilter] 
                },
                { 
                  label: 'Manager', 
                  hiddenRoles: ['manager', 'subadmin', 'affiliate'], 
                  value: subadminFilter, 
                  setter: setSubadminFilter, 
                  options: subadmins, 
                  defaultText: "All Managers", 
                  resetters: [setCollabFilter, setAdminFilter] 
                },
                { 
                  label: 'Affiliate', 
                  hiddenRoles: ['affiliate'], 
                  value: collabFilter, 
                  setter: setCollabFilter, 
                  options: collaborators, 
                  defaultText: "All Affiliates", 
                  resetters: [setSubadminFilter, setAdminFilter] 
                }
              ].map((filter, idx) => {
                if (filter.hiddenRoles.includes(currentRole)) return null;
                return (
                  <div key={idx} className="w-full sm:w-[160px] shrink-0">
                    <FormMultiSelect
                      label={filter.label}
                      selectedValues={filter.value}
                      onChange={(vals) => {
                        filter.setter(vals);
                        setPage(1);
                        if (vals.length > 0) {
                          filter.resetters.forEach((fn: any) => fn([]));
                        }
                      }}
                      placeholder={filter.defaultText}
                      options={filter.options.map((opt: any) => {
                        if (typeof opt === 'string') {
                          return { value: opt, label: opt };
                        }
                        return { value: opt.username, label: opt.displayLabel, searchText: opt.email };
                      })}
                    />
                  </div>
                );
              })}
              
              {(search || collabFilter.length > 0 || subadminFilter.length > 0 || adminFilter.length > 0 || startDate || endDate) && (
                <button
                  onClick={() => { setSearch(""); setPage(1); setCollabFilter([]); setSubadminFilter([]); setAdminFilter([]); setStartDate(""); setEndDate(""); }}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 transition-all h-[38px]"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Reset
                </button>
              )}
            </div>
          }
        />
      </div>
    </div>
  );
};

export default UserReports;
